"use client";

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  CheckCircle2,
  Clock,
  ExternalLink,
  Leaf,
  Loader2,
  MapPin,
  Phone,
  RotateCw,
  Tag,
} from "lucide-react";
import { MarketingConsentCheckbox } from "@/components/shared/MarketingConsentCheckbox";
import { capture } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

/** What the "Visit the store" block shows. Every field but `name` is optional. */
export interface OutageStoreInfo {
  name: string;
  logoUrl?: string | null;
  website?: string | null;
  address?: string | null;
  phone?: string | null;
  hours?: string[];
  promo?: string | null;
}

// The store-knowledge fields are free text written for Alice; keep the page short.
const MAX_HOURS_LINES = 8;
const MAX_PROMO_CHARS = 280;

function textLines(raw: unknown, max: number): string[] {
  if (typeof raw !== "string") return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, max);
}

function clip(raw: unknown, max: number): string | null {
  if (typeof raw !== "string") return null;
  const text = raw.trim();
  if (!text) return null;
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/**
 * Builds the store block from the designer's retailer payload
 * (`/api/retailer/{id}?designer=true`). Hours and promo come from the
 * retailer's Alice store-knowledge form (`store_hours`, `deals_and_specials`).
 * `retailer` may be null — the Plantista API can be the service that is down —
 * in which case only `fallbackName` is used, and with no name at all the block
 * is hidden.
 */
export function buildOutageStore(
  // The retailer payload is untyped across the design page.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  retailer: any | null,
  fallbackName?: string | null
): OutageStoreInfo | null {
  const rawName: string | undefined = retailer?.business_name;
  const name =
    rawName && rawName !== "Alex Garden Center" ? rawName : fallbackName || "";
  if (!name) return null;
  const addressParts = [
    retailer?.address_line1,
    retailer?.city,
    [retailer?.state, retailer?.post_code].filter(Boolean).join(" "),
  ].filter((p) => typeof p === "string" && p.trim());
  return {
    name,
    logoUrl: retailer?.branding_config?.logo_url || retailer?.logo_url || null,
    website: retailer?.website || null,
    // Only a street line makes the address useful; a bare ZIP is not.
    address: retailer?.address_line1 ? addressParts.join(", ") : null,
    phone: retailer?.business_phone || null,
    hours: textLines(retailer?.store_hours, MAX_HOURS_LINES),
    promo: clip(retailer?.deals_and_specials, MAX_PROMO_CHARS),
  };
}

function safeWebsiteUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const withScheme = /^https?:\/\//i.test(raw.trim())
    ? raw.trim()
    : `https://${raw.trim()}`;
  try {
    const url = new URL(withScheme);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, "")}`;

// Bare URLs and www. hosts in store-written text. Trailing sentence
// punctuation is left outside the link ("…/on-sale." links to "…/on-sale").
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING_PUNCT_RE = /[.,;:!?)\]}'"…]+$/;

/** Renders store-written text with every URL in it as a tappable link. */
function LinkifiedText({
  text,
  onLinkClick,
}: {
  text: string;
  onLinkClick?: () => void;
}) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    const raw = match[0];
    const linkText = raw.replace(TRAILING_PUNCT_RE, "");
    const href = linkText ? safeWebsiteUrl(linkText) : null;
    if (!href) continue;
    parts.push(text.slice(last, start));
    parts.push(
      <a
        key={start}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onLinkClick}
        className="break-all text-plantista-700 dark:text-plantista-300 underline underline-offset-4 hover:text-plantista-900 dark:hover:text-plantista-100"
      >
        {linkText}
      </a>
    );
    last = start + linkText.length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type FormState = "idle" | "submitting" | "success" | "error";

interface DesignerOutagePageProps {
  /** Plantista retailer id the waitlist signup is filed under. */
  retailerId: string;
  /** Store block contents; null hides the block (D2C / no retailer yet). */
  store: OutageStoreInfo | null;
  /** Name used in the optional marketing-consent line. */
  marketingBrand: string;
  accountType: "gc" | "d2c";
  onRetry?: () => void;
  checking?: boolean;
}

/**
 * Full-page takeover shown instead of the design flow while a blocking
 * Designer dependency is down (PLA2-947). Collects a "notify me" email and
 * sends the visitor to the store in the meantime.
 */
export function DesignerOutagePage({
  retailerId,
  store,
  marketingBrand,
  accountType,
  onRetry,
  checking = false,
}: DesignerOutagePageProps) {
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [formState, setFormState] = useState<FormState>("idle");
  const [emailError, setEmailError] = useState<string | null>(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    capture(ANALYTICS_EVENTS.DESIGNER_OUTAGE_VIEWED, {
      retailer_id: retailerId,
      account_type: accountType,
      has_store_block: !!store,
    });
  }, [retailerId, accountType, store]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || trimmed.length > 320 || !EMAIL_RE.test(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError(null);
    setFormState("submitting");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          marketingConsent,
          retailerId,
          sourcePath:
            typeof window !== "undefined"
              ? window.location.pathname
              : "/design",
        }),
      });
      if (!res.ok) throw new Error(`waitlist ${res.status}`);
      setFormState("success");
      capture(ANALYTICS_EVENTS.DESIGNER_WAITLIST_SUBMITTED, {
        retailer_id: retailerId,
        account_type: accountType,
        marketing_consent: marketingConsent,
      });
    } catch {
      setFormState("error");
    }
  };

  const website = safeWebsiteUrl(store?.website);
  const submitting = formState === "submitting";

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Headline + waitlist */}
      <section className="rounded-2xl border-2 border-plantista-950 dark:border-plantista-50 bg-white dark:bg-plantista-900 p-6 sm:p-10 shadow-[6px_6px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border-2 border-plantista-950 dark:border-plantista-50 bg-plantista-400 text-plantista-950">
          <Leaf className="h-7 w-7" aria-hidden="true" />
        </div>
        <h1 className="font-headline text-3xl sm:text-4xl font-bold leading-tight text-plantista-950 dark:text-white">
          Our garden designer is taking a short break
        </h1>
        <p className="mt-4 text-base sm:text-lg text-gray-700 dark:text-plantista-200">
          The designer is temporarily unavailable, so uploading photos and
          creating new designs is paused for the moment. It will be back soon.
          Leave your email and we&apos;ll let you know the minute it is.
        </p>

        {formState === "success" ? (
          <div
            role="status"
            className="mt-8 flex items-start gap-3 rounded-xl border-2 border-plantista-500 bg-plantista-50 dark:bg-plantista-950 p-4 text-plantista-900 dark:text-plantista-100"
          >
            <CheckCircle2
              className="mt-0.5 h-5 w-5 shrink-0 text-plantista-500"
              aria-hidden="true"
            />
            <p className="font-semibold">
              You&apos;re on the list. We&apos;ll email you as soon as the
              designer is back.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-4">
            <label
              htmlFor="outage-email"
              className="block text-sm font-bold text-plantista-950 dark:text-plantista-50"
            >
              Your email
            </label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                id="outage-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                maxLength={320}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                  if (formState === "error") setFormState("idle");
                }}
                placeholder="you@example.com"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? "outage-email-error" : undefined}
                disabled={submitting}
                className="min-w-0 flex-1 rounded-lg border-2 border-plantista-950 dark:border-plantista-50 bg-white dark:bg-plantista-950 px-4 py-3 text-base text-plantista-950 dark:text-plantista-50 placeholder:text-gray-400 dark:placeholder:text-plantista-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-plantista-500 disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center justify-center gap-2 rounded-lg border-2 border-plantista-950 bg-plantista-400 px-6 py-3 font-bold text-plantista-950 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 transition-all hover:bg-plantista-500 dark:hover:bg-plantista-300 active:translate-x-[2px] active:translate-y-[2px] active:shadow-[2px_2px_0px_0px] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting && (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                Notify me when it&apos;s back
              </button>
            </div>
            {emailError && (
              <p
                id="outage-email-error"
                className="text-sm font-medium text-red-600 dark:text-red-400"
              >
                {emailError}
              </p>
            )}
            <p className="text-xs text-gray-600 dark:text-plantista-300">
              We&apos;ll use this email to tell you when the designer is back.
            </p>
            <MarketingConsentCheckbox
              id="outage-marketing-consent"
              checked={marketingConsent}
              onChange={setMarketingConsent}
              disabled={submitting}
              label={`Also send me offers and news from ${marketingBrand}.`}
            />
            {formState === "error" && (
              <p
                role="alert"
                className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/60 dark:bg-amber-900/20 dark:text-amber-100"
              >
                We couldn&apos;t save your email right now — please visit us in
                store or try again later.
              </p>
            )}
          </form>
        )}

        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={checking}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-plantista-700 dark:text-plantista-300 underline-offset-4 hover:underline disabled:opacity-60"
          >
            {checking ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <RotateCw className="h-4 w-4" aria-hidden="true" />
            )}
            Check again
          </button>
        )}
      </section>

      {/* Visit the store */}
      {store && (
        <section className="rounded-2xl border-2 border-plantista-950 dark:border-plantista-50 bg-white dark:bg-plantista-900 p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-wider text-plantista-600 dark:text-plantista-400">
            In the meantime, visit the store
          </p>
          <div className="mt-4 flex items-center gap-4">
            {store.logoUrl && (
              // Retailer logos come from arbitrary hosts; next/image would need
              // every one in remotePatterns.
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={store.logoUrl}
                alt=""
                className="h-14 w-14 shrink-0 rounded-lg border border-gray-200 dark:border-plantista-700 bg-white object-contain p-1"
              />
            )}
            <h2 className="font-headline text-2xl font-bold text-plantista-950 dark:text-white">
              {store.name}
            </h2>
          </div>

          <ul className="mt-6 space-y-3 text-sm text-gray-700 dark:text-plantista-200">
            {store.address && (
              <li className="flex items-start gap-3">
                <MapPin
                  className="mt-0.5 h-4 w-4 shrink-0 text-plantista-500"
                  aria-hidden="true"
                />
                <span>{store.address}</span>
              </li>
            )}
            {store.phone && (
              <li className="flex items-start gap-3">
                <Phone
                  className="mt-0.5 h-4 w-4 shrink-0 text-plantista-500"
                  aria-hidden="true"
                />
                <a
                  href={telHref(store.phone)}
                  className="font-semibold text-plantista-700 dark:text-plantista-300 underline-offset-4 hover:underline"
                >
                  {store.phone}
                </a>
              </li>
            )}
            {store.hours && store.hours.length > 0 && (
              <li className="flex items-start gap-3">
                <Clock
                  className="mt-0.5 h-4 w-4 shrink-0 text-plantista-500"
                  aria-hidden="true"
                />
                <span className="space-y-0.5">
                  {store.hours.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </span>
              </li>
            )}
          </ul>

          {store.promo && (
            <p className="mt-6 flex items-start gap-3 rounded-xl border-2 border-dashed border-plantista-500 bg-plantista-50 dark:bg-plantista-950 p-4 text-sm font-semibold text-plantista-900 dark:text-plantista-100">
              <Tag
                className="mt-0.5 h-4 w-4 shrink-0 text-plantista-500"
                aria-hidden="true"
              />
              <span className="min-w-0 whitespace-pre-line">
                <LinkifiedText
                  text={store.promo}
                  onLinkClick={() =>
                    capture(ANALYTICS_EVENTS.DESIGNER_OUTAGE_WEBSITE_CLICKED, {
                      retailer_id: retailerId,
                      account_type: accountType,
                      link: "promo",
                    })
                  }
                />
              </span>
            </p>
          )}

          {website && (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                capture(ANALYTICS_EVENTS.DESIGNER_OUTAGE_WEBSITE_CLICKED, {
                  retailer_id: retailerId,
                  account_type: accountType,
                  link: "website",
                })
              }
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 border-plantista-950 bg-white dark:bg-plantista-950 px-6 py-3 font-bold text-plantista-950 dark:text-plantista-50 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 dark:border-plantista-50 transition-all hover:bg-plantista-50 dark:hover:bg-plantista-800 active:translate-x-[2px] active:translate-y-[2px] active:shadow-[2px_2px_0px_0px] sm:w-auto"
            >
              Visit our website
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          )}
        </section>
      )}
    </div>
  );
}
